import React, { useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { useForm } from "react-hook-form";
import { Button } from "@components/ui/Button";
import { Input } from "@components/ui/Input";
import { Card, CardBody } from "@components/ui/Card/Card";

export type NewPasswordFormData = {
  password: string;
};

type NewPasswordProps = {
  onNewPassword: (data: NewPasswordFormData) => Promise<void>;
} & RouteComponentProps;

export function NewPassword({ onNewPassword }: NewPasswordProps) {
  const [error, setError] = useState<string | null>();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewPasswordFormData>({
    mode: "onBlur",
  });

  const onSubmit = async (data: NewPasswordFormData) => {
    setError(null);

    try {
      await onNewPassword(data);
      reset();
    } catch (error) {
      if (error.message) {
        setError(error.message);
      } else {
        // TODO report this error somewhere
        console.error(error);
        setError("An unknown error occurred, please reach out to support.");
      }
    }
  };

  return (
    <Card title="Enter a new password">
      <CardBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="mt-1 text-xs text-gray-500">
                Password must be 12 characters and contain at least one
                uppercase, lowercase, number, and symbol.
              </div>
              <div className="mt-1">
                <Input
                  type="password"
                  {...register("password", {
                    required: {
                      value: true,
                      message: "Password is required",
                    },
                    minLength: {
                      value: 12,
                      message: "Password must have at least 12 characters",
                    },
                  })}
                  error={errors.password?.message}
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <div>
              <Button size="lg" type="submit" disabled={false}>
                Submit
              </Button>
            </div>
          </div>
        </form>
        {error && <div className="mt-6 text-red-600">{error}</div>}
      </CardBody>
    </Card>
  );
}
